import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserX, UserCheck } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { getDispatcherDetail, editDispatcher, deactivateDispatcher } from '../api/dispatchersApi';
import { useToast } from '../context/ToastContext';

function DispatcherDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [dispatcher, setDispatcher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchDispatcher() {
      try {
        const result = await getDispatcherDetail(id);
        if (result.success) {
          setDispatcher(result.dispatcher);
          setForm({
            name: result.dispatcher.name,
            email: result.dispatcher.email,
            phone: result.dispatcher.phone,
            password: '',
          });
        } else {
          showToast('error', result.message || 'Failed to load dispatcher.');
        }
      } catch (err) {
        showToast('error', 'Could not connect to server.');
      } finally {
        setLoading(false);
      }
    }
    fetchDispatcher();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { id_dispatcher: id, name: form.name, email: form.email, phone: form.phone };
      if (form.password) payload.password = form.password;

      const result = await editDispatcher(payload);
      if (result.success) {
        showToast('success', 'Dispatcher updated successfully.');
        navigate('/dispatchers');
      } else {
        showToast('error', result.message || 'Failed to update dispatcher.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    const newStatus = dispatcher.is_active ? 0 : 1;
    try {
      const result = await deactivateDispatcher(id, newStatus);
      if (result.success) {
        setDispatcher((prev) => ({ ...prev, is_active: newStatus }));
        showToast('success', newStatus ? 'Dispatcher reactivated.' : 'Dispatcher deactivated.');
      } else {
        showToast('error', result.message || 'Failed to update dispatcher status.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    }
  };

  if (loading || !dispatcher) {
    return (
      <div style={{ display: 'flex' }}>
        <Sidebar />
        <div style={{ flex: 1 }}>
          <TopBar title="Loading..." />
          <LoadingSpinner fullScreen />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar
          title={dispatcher.name}
          rightAction={
            <button
              onClick={handleToggleActive}
              style={{
                ...styles.toggleBtn,
                backgroundColor: dispatcher.is_active ? '#fee2e2' : '#d1fae5',
                color: dispatcher.is_active ? '#991b1b' : '#065f46',
              }}
            >
              {dispatcher.is_active ? (
                <>
                  <UserX size={15} style={{ marginRight: 6 }} /> Deactivate
                </>
              ) : (
                <>
                  <UserCheck size={15} style={{ marginRight: 6 }} /> Reactivate
                </>
              )}
            </button>
          }
        />

        <div style={{ padding: 24, maxWidth: 480 }}>
          {!dispatcher.is_active && (
            <div style={styles.inactiveBanner}>
              This dispatcher is currently deactivated and cannot log in.
            </div>
          )}

          <form onSubmit={handleSave} style={styles.card}>
            <label style={styles.label}>Full Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={styles.input}
              required
            />

            <label style={styles.label}>Email</label>
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              type="email"
              style={styles.input}
              required
            />

            <label style={styles.label}>Phone</label>
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              style={styles.input}
            />

            <label style={styles.label}>New Password (leave blank to keep current)</label>
            <input
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              type="password"
              style={styles.input}
            />

            <div style={styles.buttonRow}>
              <button type="button" onClick={() => navigate('/dispatchers')} style={styles.cancelBtn}>
                Cancel
              </button>
              <button type="submit" disabled={saving} style={styles.submitBtn}>
                {saving ? '...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const styles = {
  toggleBtn: {
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  inactiveBanner: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 16,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
    marginTop: 14,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 24,
  },
  cancelBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    color: '#374151',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
};

export default DispatcherDetail;